import { useState } from "react";

import { api, ApiError } from "../lib/api";
import { useAuth } from "../context/auth";
import { useDateTime } from "../lib/use-relative";
import { useT } from "../context/locale";
import type { TranslationKey } from "../i18n";
import { useToast } from "../context/toast";
import {} from "../lib/format";
import { Badge, Button, ErrorNote, Panel } from "../components/ui";
import { TextField } from "../components/ui/form";

/** Your own account: who you are signed in as, and your password. */
export function Settings() {
  const dateTime = useDateTime();
  const t = useT();
  const { admin, signOut } = useAuth();
  const toast = useToast();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mismatch = confirm !== "" && next !== confirm;
  const valid =
    current.length > 0 &&
    next.length >= 8 &&
    next === confirm &&
    next !== current;

  async function changePassword() {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await api.changePassword({
        currentPassword: current,
        newPassword: next,
        confirmPassword: confirm,
      });
      toast.success(
        t("toast.passwordChanged"),
        t("toast.passwordDetail"),
      );
      setCurrent("");
      setNext("");
      setConfirm("");
      // The old session stays valid, but ending it is the honest behaviour
      // after a credential change.
      window.setTimeout(() => signOut("manual"), 1200);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.changePassword"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-5">
      <Panel eyebrow={t("sessions.principal")} title={t("settings.yourAccount")}>
        <dl className="grid gap-x-8 gap-y-3 text-[13px] sm:grid-cols-2">
          <Row
            label={t("common.username")}
            value={<span className="money">{admin?.username}</span>}
          />
          <Row label={t("field.displayName")} value={admin?.displayName} />
          <Row label={t("common.role")} value={
              <Badge tone="gold">
                {admin ? t(`admins.role.${admin.role}` as TranslationKey) : "—"}
              </Badge>
            } />
          <Row label={t("col.lastSignIn")} value={dateTime(admin?.lastLoginAt)} />
        </dl>
      </Panel>

      <Panel eyebrow={t("sessions.principal")} title={t("audit.admin.password_change")}>
        <div className="space-y-4">
          <p className="text-muted text-[13px]">
{t("settings.currentExplain")}
          </p>

          <TextField
            label={t("settings.current")}
            value={current}
            onChange={setCurrent}
            type="password"
            required
            autoComplete="current-password"
          />
          <TextField
            label={t("settings.new")}
            value={next}
            onChange={setNext}
            type="password"
            hint={t("field.minPassword")}
            error={next && next.length < 8 ? t("field.minPassword") : null}
            required
            autoComplete="new-password"
          />
          <TextField
            label={t("settings.confirm")}
            value={confirm}
            onChange={setConfirm}
            type="password"
            error={mismatch ? t("error.passwordsDiffer") : null}
            required
            autoComplete="new-password"
          />

          {error ? <ErrorNote message={error} /> : null}

          <div className="flex justify-end">
            <Button
              variant="gold"
              onClick={changePassword}
              loading={busy}
              disabled={!valid}
            >
              {t("audit.admin.password_change")}
            </Button>
          </div>
        </div>
      </Panel>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd className="text-muted mt-1">{value}</dd>
    </div>
  );
}
